import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Identifier } from '@okletsplay/core';
import { MatchEntity } from 'app/interfaces/entity';
import { MatchPlayerStatus, MatchStatus } from '@okletsplay/core/src/modules/match/enums';
import { EntityStoreName } from 'app/enums/store';
import { dispatchUnwrap } from 'app/utils/store';
import classNames from 'classnames';
import { gamesSelectorFactory } from 'modules/games/store/games/selectors';
import { CurrentMatchStoreStatus } from 'modules/matches/store/current-match/enums';
import {
  acceptInvite,
  declineInvite,
  joinMatchRequest,
} from 'modules/matches/store/current-match/repository';
import { useDispatch, useSelector } from 'react-redux';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { usePlatformLinkAction } from 'shared/hooks';
import { ActionGuard } from 'shared/components/ActionGuard';
import { useStyles } from './MatchModalPlayerActions.styles';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { useMatchModal } from 'modules/matches/hooks';
import { WithPaidActionButton } from 'modules/game-payments/components';
import { MATCH_PLAYERS_DEFAULT_SETTINGS } from 'modules/matches/constants';
import { MatchLeaderboardModalActions } from '../../MatchLeaderboardModal/MatchLeaderboardModalActions';
import { toastSuccess } from 'modules/utils';

interface MatchActionsProps {
  match?: MatchEntity;
  playerStatus?: MatchPlayerStatus;
}

export const MatchModalPlayerActions: FC<MatchActionsProps> = ({ match, playerStatus }) => {
  const matchId = match?.id;
  const entryFee = match?.entryFee ?? 0;
  const gameId = match?.game?.id ?? '';
  const matchStatus = match?.status;
  const isJoinApproveRequired = match?.isJoinApproveRequired;

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const { closeMatchModal } = useMatchModal();

  const game = useSelector(gamesSelectorFactory.useById(gameId));
  const { openPlatformLink, isPlatformLinked } = usePlatformLinkAction(game?.platformType);

  const isDeclining = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Declining,
  });

  const isAccepting = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Accepting,
  });

  const isJoining = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Joining,
  });

  const joinRequestHandler = useCallback(() => {
    if (!isPlatformLinked) {
      openPlatformLink();
      return;
    }

    dispatchUnwrap(joinMatchRequest(matchId as Identifier), {
      success: () => {
        const message = isJoinApproveRequired
          ? 'Join request has been sent.'
          : 'You joined the match.';

        toastSuccess(message);
      },
    });
  }, [isJoinApproveRequired, isPlatformLinked, matchId, openPlatformLink]);

  const acceptInviteHandler = useCallback(() => {
    if (!isPlatformLinked) {
      openPlatformLink();
      return;
    }

    dispatchUnwrap(acceptInvite(matchId as Identifier));
  }, [isPlatformLinked, matchId, openPlatformLink]);

  const declineInviteHandler = useCallback(() => {
    dispatch(declineInvite(matchId as Identifier));
    closeMatchModal();
  }, [dispatch, matchId, closeMatchModal]);

  const isMaxPlayersCountReached = useMemo(() => {
    if (match?.players === undefined) return;

    const acceptedPlayers = match.players.filter(({ status }) =>
      [MatchPlayerStatus.Ready, MatchPlayerStatus.Played].includes(status)
    );

    const playersSettings = match.playersSettings ?? MATCH_PLAYERS_DEFAULT_SETTINGS;

    return acceptedPlayers.length >= playersSettings?.maxPlayersCount;
  }, [match?.players, match?.playersSettings]);

  const getJoinButton = useCallback(
    (label: string, onJoin: () => void, isLoading: boolean) => (
      <ActionGuard>
        <WithPaidActionButton
          label={label}
          tooltipText="Join Match"
          isLoading={isLoading}
          amount={entryFee}
          onClick={onJoin}
        >
          {({ isCanNotEnter: isCannotPayToEnter, text, onClick }) => (
            <ActionGuard>
              <LoaderButton
                size="medium"
                color="primary"
                variant="contained"
                loading={isLoading}
                onClick={onClick}
                className={classes.button}
                disabled={
                  isAccepting ||
                  isJoining ||
                  isDeclining ||
                  isCannotPayToEnter ||
                  isMaxPlayersCountReached
                }
              >
                {text}
              </LoaderButton>
            </ActionGuard>
          )}
        </WithPaidActionButton>
      </ActionGuard>
    ),
    [classes.button, entryFee, isAccepting, isDeclining, isJoining, isMaxPlayersCountReached]
  );

  const getJoinActions = useCallback(
    (...params: Parameters<typeof getJoinButton>) => {
      if (!match) return null;

      const joinButton = getJoinButton(...params);
      return isMaxPlayersCountReached ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {joinButton}
          <Typography sx={{ alignSelf: 'center' }} className={classes.text}>
            Match is full. Max number of players joined.
          </Typography>
        </Box>
      ) : (
        joinButton
      );
    },
    [classes.text, getJoinButton, isMaxPlayersCountReached, match]
  );

  const joinButton = (
    <Box className={classes.buttonContainer}>
      {getJoinActions('Join', joinRequestHandler, isJoining)}
    </Box>
  );

  const playerActions: Record<string, ReactElement> = {
    [MatchPlayerStatus.Requested]: (
      <Typography className={classes.text} variant="body1">
        Waiting for the host to accept your join request.
      </Typography>
    ),
    [MatchPlayerStatus.Ready]: matchId ? (
      <MatchLeaderboardModalActions
        matchId={matchId}
        playerStatus={MatchPlayerStatus.Ready}
        hideForfeit
      />
    ) : (
      <Typography className={classes.text} variant="body1">
        Waiting for the match start.
      </Typography>
    ),
    [MatchPlayerStatus.Invited]: (
      <Box className={classes.buttonContainer}>
        <ActionGuard>
          <LoaderButton
            size="medium"
            color="primary"
            variant="outlined"
            loading={isDeclining}
            onClick={declineInviteHandler}
            className={classNames(classes.button, classes.decline)}
            disabled={isDeclining || isAccepting}
          >
            Decline
          </LoaderButton>
        </ActionGuard>
        {getJoinActions('Accept', acceptInviteHandler, isAccepting)}
      </Box>
    ),
    [MatchPlayerStatus.Played]: (
      <Typography className={classes.text} variant="body1">
        The score is submitted, waiting for other players to join and play.
      </Typography>
    ),
    [MatchPlayerStatus.Declined]: joinButton,
  };

  if (matchStatus === MatchStatus.Declined) {
    return (
      <Typography className={classes.text} variant="body1">
        This match has been declined.
      </Typography>
    );
  }

  return playerStatus ? playerActions[playerStatus] : joinButton;
};
